import { Box, Tooltip, useTheme } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { tokens } from '../../../global/theme/tokens';
import moment from 'moment';
import 'moment/locale/de';
import {
  dayOfYear,
  daysInYear,
  groupConsecutiveDays,
  getMonthsOfYear,
} from './vacationUtils';
import { COMPANY_HOLIDAY_COLOR } from './CompanyHolidayRow';

export const VacationTimeline = ({
  sickDays = [],
  year,
  companyHolidays = [],
  onClickVacation,
  employeeRequests = [],
}) => {
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const colors = tokens(theme.palette.mode, theme.palette.colorTheme);

  const locale = i18n.language || 'de';
  const totalDays = daysInYear(year);
  const months = getMonthsOfYear(year, locale);
  const formatDate = (date) =>
    moment(date).locale(locale).format('dd, DD.MM.YYYY');
  const todayIndex = dayOfYear(moment().format('YYYY-MM-DD'), year);
  const isCurrentYear = moment().year() === year;

  const toPercent = (days) => (days / totalDays) * 100;

  const sickRanges = groupConsecutiveDays(sickDays);

  // build visible request items, then split into overlap clusters so only the overlapping ones share lanes.
  // non-overlapping requests stay at full height.
  const items = (employeeRequests || [])
    .filter(
      (r) => r && r.status !== 2 && r.values?.startDate && r.values?.endDate,
    )
    .map((r) => ({
      request: r,
      rawStart: dayOfYear(r.values.startDate, year),
      rawEnd: dayOfYear(r.values.endDate, year),
    }))
    .filter((it) => it.rawEnd >= 0 && it.rawStart < totalDays)
    .sort((a, b) => a.rawStart - b.rawStart || a.rawEnd - b.rawEnd);

  const parent = items.map((_, i) => i);
  const find = (x) => (parent[x] === x ? x : (parent[x] = find(parent[x])));
  for (let i = 0; i < items.length; i++) {
    for (let j = i + 1; j < items.length; j++) {
      if (items[j].rawStart > items[i].rawEnd) break;
      parent[find(i)] = find(j);
    }
  }

  const clusters = new Map();
  items.forEach((it, i) => {
    const root = find(i);
    if (!clusters.has(root)) clusters.set(root, []);
    clusters.get(root).push(it);
  });

  const laneItems = [];
  clusters.forEach((group) => {
    const laneEnds = [];
    group.forEach((it) => {
      let lane = laneEnds.findIndex((end) => end < it.rawStart);
      if (lane === -1) {
        lane = laneEnds.length;
        laneEnds.push(it.rawEnd);
      } else {
        laneEnds[lane] = it.rawEnd;
      }
      it.lane = lane;
    });
    const laneCount = laneEnds.length;
    group.forEach((it) => laneItems.push({ ...it, laneCount }));
  });

  return (
    <Box
      sx={{
        position: 'relative',
        width: '100%',
        height: '100%',
        minHeight: 50,
      }}
    >
      {/* Betriebsferien-Bänder */}
      {companyHolidays.map((holiday, i) => {
        const rawStart = dayOfYear(holiday.startDate, year);
        const rawEnd = dayOfYear(holiday.endDate, year);
        if (rawEnd < 0 || rawStart >= totalDays) return null;
        const startIdx = Math.max(rawStart, 0);
        const endIdx = Math.min(rawEnd, totalDays - 1);
        const span = endIdx - startIdx + 1;
        return (
          <Tooltip
            key={`ch-${holiday.id ?? i}`}
            title={`${t('Company Holiday')}: ${formatDate(holiday.startDate)} – ${formatDate(holiday.endDate)}`}
          >
            <Box
              onDoubleClick={(e) => e.stopPropagation()}
              sx={{
                position: 'absolute',
                left: `${toPercent(startIdx)}%`,
                width: `${toPercent(span)}%`,
                top: 0,
                height: '100%',
                zIndex: 4,
                pointerEvents: 'auto',
                cursor: 'default',
              }}
            >
              <Box
                sx={{
                  position: 'absolute',
                  inset: 0,
                  bgcolor: COMPANY_HOLIDAY_COLOR,
                  opacity: 0.07,
                  borderRadius: '3px',
                }}
              />
              <Box
                sx={{
                  position: 'absolute',
                  left: 0,
                  right: 0,
                  top: '20%',
                  height: '60%',
                  bgcolor: COMPANY_HOLIDAY_COLOR,
                  borderRadius: '3px',
                }}
              />
            </Box>
          </Tooltip>
        );
      })}

      {/* Monats-Separatoren */}
      {months.map((month, i) => (
        <Box
          key={i}
          sx={{
            position: 'absolute',
            left: `${toPercent(month.startDay)}%`,
            top: 0,
            bottom: 0,
            borderLeft: `1px solid ${colors.selected}`,
          }}
        />
      ))}

      {/* Heute-Markierung */}
      {isCurrentYear && todayIndex >= 0 && todayIndex < totalDays && (
        <Box
          sx={{
            position: 'absolute',
            left: `${toPercent(todayIndex)}%`,
            top: 0,
            bottom: 0,
            width: 2,
            bgcolor: colors.info,
            zIndex: 3,
          }}
        />
      )}

      {/* Urlaubs-Blöcke */}
      {laneItems.map(({ request, rawStart, rawEnd, lane, laneCount }, i) => {
        const laneHeight = 60 / Math.max(laneCount, 1);
        const { startDate, endDate } = request.values;
        const startIdx = Math.max(rawStart, 0);
        const endIdx = Math.min(rawEnd, totalDays - 1);
        const span = endIdx - startIdx + 1;
        const isAccepted = request.status === 1;
        const isEditable = !!onClickVacation;
        const bgcolor = isAccepted ? colors.success : colors.warning;
        const label =
          request.type === 'timeCompensation'
            ? t('Time Compensation')
            : t('Vacation');
        return (
          <Tooltip
            key={`v-${request.id ?? i}`}
            title={`${label}: ${formatDate(startDate)} – ${formatDate(endDate)} (${span} ${t('Days')})`}
          >
            <Box
              onClick={
                isEditable
                  ? (e) => {
                      e.stopPropagation();
                      onClickVacation(request, e.currentTarget);
                    }
                  : undefined
              }
              sx={{
                position: 'absolute',
                left: `${toPercent(startIdx)}%`,
                width: `${toPercent(span)}%`,
                top: `${20 + lane * laneHeight}%`,
                height: `${laneHeight}%`,
                bgcolor,
                opacity: isAccepted ? 0.85 : 0.7,
                border: !isAccepted ? `1px dashed ${colors.warning}` : 'none',
                borderRadius: '3px',
                zIndex: 2,
                cursor: isEditable ? 'pointer' : 'default',
                '&:hover': isEditable ? { opacity: 1 } : undefined,
              }}
            />
          </Tooltip>
        );
      })}

      {/* Krankheits-Blöcke */}
      {sickRanges.map((range, i) => {
        const startIdx = dayOfYear(range.startDate, year);
        const span = range.days.length;
        return (
          <Tooltip
            key={`s-${i}`}
            title={`${t('Sick')}: ${formatDate(range.startDate)} – ${formatDate(range.endDate)} (${span} ${t('Days')})`}
          >
            <Box
              sx={{
                position: 'absolute',
                left: `${toPercent(startIdx)}%`,
                width: `${toPercent(span)}%`,
                top: '20%',
                height: '60%',
                bgcolor: colors.error,
                opacity: 0.95,
                borderRadius: '3px',
                zIndex: 4,
                cursor: 'pointer',
              }}
            />
          </Tooltip>
        );
      })}
    </Box>
  );
};
